import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { SkipBack, Play, Pause, SkipForward } from "lucide-react";
import { PricingEvent } from "@/store/eventsSlice";
import { formatVersionDate } from "@/utils/dateUtils";

interface EventImageViewerProps {
  company: PricingEvent;
}

const EventImageViewer = ({ company }: EventImageViewerProps) => {
  const [isShowingBefore, setIsShowingBefore] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const [speed, setSpeed] = useState(3);

  const toggleAnimation = useCallback(() => {
    setIsPlaying(prev => !prev);
  }, []);

  const manualToggle = useCallback((showBefore: boolean) => {
    setIsShowingBefore(showBefore);
    setIsPlaying(false);
  }, []);

  const handleSpeedChange = useCallback((value: number[]) => {
    setSpeed(value[0]);
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isPlaying) {
      interval = setInterval(() => {
        setIsShowingBefore(prev => !prev);
      }, speed * 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isPlaying, speed]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between gap-4 bg-[#f3f3f3] border border-[#8A898C] rounded-lg p-3 mb-4 shrink-0">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => manualToggle(true)}
            disabled={isShowingBefore}
            className="rounded-lg"
          >
            <SkipBack className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={toggleAnimation}
            className="rounded-lg"
          >
            {isPlaying ? (
              <Pause className="h-4 w-4" />
            ) : (
              <Play className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => manualToggle(false)}
            disabled={!isShowingBefore}
            className="rounded-lg"
          >
            <SkipForward className="h-4 w-4" />
          </Button>
        </div>

        <div className="text-sm text-gray-600 font-bold">
          {formatVersionDate(isShowingBefore ? company.v1 : company.v2)}
        </div>

        <div className="flex items-center gap-3 min-w-[300px]">
          <span className="text-sm text-gray-700 whitespace-nowrap">Animation Speed</span>
          <div className="flex items-center gap-2 flex-1">
            <Slider
              defaultValue={[3]}
              min={1}
              max={5}
              step={0.5}
              value={[speed]}
              onValueChange={handleSpeedChange}
              className="flex-1"
            />
            <span className="text-sm text-gray-600 whitespace-nowrap">{speed}s</span>
          </div>
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto">
        <img
          src={isShowingBefore ? company.beforeUrl : company.afterUrl}
          alt={`${company.slug} ${isShowingBefore ? 'before' : 'after'} pricing page`}
          className="w-full h-auto max-w-full"
          loading="lazy"
        />
      </div>
    </div>
  );
};

export default EventImageViewer;